/* STIKKYS booking flow, one step per page. Selections persist in
   localStorage; each page boots from the store, validates prerequisites,
   and renders its step plus the shared summary. No dependencies. */
(() => {
  const SELECTOR = '[data-booking]:not([data-booking-ready])';
  const STORE_KEY = 'stikkys-booking-v1';
  const RECEIPT_KEY = 'stikkys-booking-receipt-v1';
  const RECEIPT_TTL = 7 * 24 * 3600 * 1000;
  const MAX_DAYS_OUT = 365;
  const STEP_URLS = { 1: '/pages/book', 2: '/pages/book-vehicle', 3: '/pages/book-schedule', 4: '/pages/book-confirm' };
  const WINDOW_TIMES = { Morning: '7–11am', Midday: '11am–2pm', Afternoon: '2–6pm', Flexible: 'Any time' };
  const AU_MOBILE_MESSAGE = 'Enter a 10-digit Australian mobile number, such as 0412 345 678.';

  function pad(number) {
    return String(number).padStart(2, '0');
  }

  function isoDay(offset) {
    const date = new Date();
    date.setDate(date.getDate() + offset);
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
  }

  function fmtDate(iso) {
    if (!iso) return '';
    const parts = String(iso).split('-');
    if (parts.length !== 3) return iso;
    const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
  }

  function monthKeyOf(year, month) {
    return year * 12 + month;
  }

  function isoOf(date) {
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
  }

  function addMonthsISO(base, months) {
    const first = new Date(base.getFullYear(), base.getMonth() + months, 1);
    const lastDay = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    first.setDate(Math.min(base.getDate(), lastDay));
    return isoOf(first);
  }

  function loadStore() {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORE_KEY));
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_error) {
      return {};
    }
  }

  function saveStore(patch) {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(Object.assign(loadStore(), patch)));
    } catch (_error) {
      /* private mode: flow still works for this page view */
    }
  }

  function storageWorks() {
    try {
      localStorage.setItem(STORE_KEY + '-probe', '1');
      localStorage.removeItem(STORE_KEY + '-probe');
      return true;
    } catch (_error) {
      return false;
    }
  }

  function loadReceipt() {
    try {
      const parsed = JSON.parse(localStorage.getItem(RECEIPT_KEY));
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (_error) {
      return null;
    }
  }

  function saveReceipt(receipt) {
    try {
      localStorage.setItem(RECEIPT_KEY, JSON.stringify(receipt));
    } catch (_error) {
      /* private mode: the email still sends, step 4 just cannot recap */
    }
  }

  function receiptValid(receipt) {
    if (!receipt || typeof receipt !== 'object') return false;
    if (!receipt.ref || !receipt.service || !receipt.vehicle || !receipt.sched) return false;
    const age = Date.now() - Number(receipt.sentAt || 0);
    return age >= 0 && age < RECEIPT_TTL;
  }

  function makeReference() {
    const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 4; i += 1) {
      code += alphabet.charAt(Math.floor(Math.random() * alphabet.length));
    }
    return 'STK-' + code;
  }

  function locationText(s) {
    if (!s) return '';
    if (s.location === 'mobile') {
      return (s.suburb || '').trim() ? 'Mobile — ' + String(s.suburb).trim() : 'Mobile (suburb missing)';
    }
    if (s.location === 'inhouse') return 'In-house';
    return '';
  }

  function schedValidOf(s) {
    if (!s || !s.date1 || !s.window1 || !s.location) return false;
    if (s.location === 'mobile' && !auPostcodeValid(s.suburb)) return false;
    return true;
  }

  function missingSchedItems(s) {
    const missing = [];
    if (!s.date1) missing.push('a date');
    if (!s.window1) missing.push('a time window');
    if (!s.location) missing.push('a location');
    else if (s.location === 'mobile' && !auPostcodeValid(s.suburb)) missing.push('a valid 4-digit postcode');
    return missing;
  }

  function auPostcodeValid(value) {
    return /^[0-9]{4}$/.test(String(value || '').trim());
  }

  function postcodeDigits(value) {
    return String(value || '').replace(/[^0-9]/g, '').slice(0, 4);
  }

  function auMobileValid(value) {
    const compact = String(value || '').replace(/[\s-]/g, '');
    return /^04[0-9]{8}$/.test(compact);
  }

  function capDigits(value, maximum) {
    let digits = 0;
    return String(value || '')
      .split('')
      .filter((character) => {
        if (!/[0-9]/.test(character)) return true;
        digits += 1;
        return digits <= maximum;
      })
      .join('');
  }

  function init(root) {
    root.setAttribute('data-booking-ready', 'true');
    const step = Number(root.getAttribute('data-step')) || 1;

    let data = { services: [], vehicles: [] };
    const dataEl = root.querySelector('[data-booking-data]');
    if (dataEl) {
      try {
        data = Object.assign(data, JSON.parse(dataEl.textContent));
      } catch (_error) {
        /* leave defaults; flow renders empty states */
      }
    }

    const store = loadStore();
    const inEditor = Boolean(window.Shopify && window.Shopify.designMode);
    if (step !== 4 && step >= 2 && !store.service) {
      window.location.replace(STEP_URLS[1] + '?need=service');
      return;
    }
    if (step === 3 && !store.vehicle) {
      window.location.replace(STEP_URLS[2] + '?need=vehicle');
      return;
    }
    /* Step 4 boots from the frozen receipt, never the working store: drop
       the old picks (back-button travellers cannot resubmit) and land at
       the top, since the submit/CAPTCHA return hop can carry a #fragment
       that would otherwise yank the fresh page down. */
    let receipt = null;
    if (step === 4) {
      receipt = loadReceipt();
      if (!inEditor) {
        /* Storage-denied browsers cannot hold a receipt at all: the email
           still sent, so show the static confirmation rather than bouncing
           a successful submitter back to step 1. */
        if (!receiptValid(receipt) && storageWorks()) {
          window.location.replace(STEP_URLS[1]);
          return;
        }
        try {
          localStorage.removeItem(STORE_KEY);
        } catch (_error) {
          /* storage already unavailable; the in-memory reset below still applies */
        }
        store.service = null;
        store.vehicle = null;
        store.interest = null;
        store.sched = null;
        try {
          window.scrollTo(0, 0);
        } catch (_error) {
          /* leave the scroll position where it is */
        }
        try {
          if (window.location && window.location.hash) {
            window.history.replaceState(null, '', (window.location.pathname || '') + (window.location.search || ''));
          }
        } catch (_error) {
          /* leave the URL alone */
        }
      }
    }

    const serviceById = (id) => (data.services || []).find((s) => s.id === id);
    const vehicleById = (id) => (data.vehicles || []).find((v) => v.id === id);
    /* Family-branch map: each detailing-services option deep-links with its
       family (?service=standard|correction|ceramic); match it against the
       quote form's branch titles, falling back to the block id. */
    const serviceFromParam = (param) => {
      const needle = String(param || '').trim().toLowerCase();
      if (!needle) return null;
      const services = data.services || [];
      return services.find((s) => String(s.title || '').toLowerCase().indexOf(needle) !== -1)
        || services.find((s) => String(s.id || '').toLowerCase().indexOf(needle) !== -1)
        || null;
    };
    if (store.service && step === 1 && !serviceById(store.service.id)) store.service = null;
    if (store.vehicle && step === 2 && !vehicleById(store.vehicle.id)) store.vehicle = null;

    const sched = Object.assign(
      { date1: '', window1: '', date2: '', window2: '', location: null, suburb: '' },
      store.sched || {}
    );
    sched.suburb = postcodeDigits(sched.suburb);
    let selectedServiceId = store.service ? store.service.id : null;
    let selectedVehicleId = store.vehicle ? store.vehicle.id : null;
    /* A fresh CTA click is the newest explicit intent, so the deep link wins
       over any stale saved pick; strip it after consuming so a later manual
       pick survives refresh. Other params (e.g. need=) are preserved. */
    if (step === 1) {
      let deepParam = null;
      let deepOption = null;
      try {
        const incoming = new URLSearchParams(window.location.search);
        deepParam = incoming.get('service');
        deepOption = incoming.get('option');
      } catch (_error) {
        deepParam = null;
        deepOption = null;
      }
      const deepFound = serviceFromParam(deepParam);
      if (deepFound) {
        selectedServiceId = deepFound.id;
        store.service = {
          id: deepFound.id,
          title: deepFound.title,
          desc: deepFound.desc,
          includes: deepFound.includes,
          duration: deepFound.duration
        };
        /* The tapped option rides with its branch; it only counts when the
           branch resolves, so a lone option param never shows under an
           empty service. */
        const interestText = String(deepOption || '').trim();
        store.interest = interestText ? { text: interestText, serviceId: deepFound.id } : null;
        saveStore({ service: store.service, interest: store.interest });
        try {
          const params = new URLSearchParams(window.location.search);
          params.delete('service');
          params.delete('option');
          const rest = params.toString();
          window.history.replaceState(null, '', (window.location.pathname || STEP_URLS[1]) + (rest ? '?' + rest : ''));
        } catch (_error) {
          /* leave the URL alone; the pick is already persisted */
        }
      }
    }
    let activePref = 1;

    const pane3 = root.querySelector('[data-pane="3"]');
    const leadDays = Math.max(0, Number(pane3 ? pane3.getAttribute('data-lead-days') : 2) || 0);
    let todayISO = '';
    let minISO = '';
    let maxISO = '';
    let selectMaxISO = '';
    let minKey = 0;
    let maxKey = 0;
    const view = { y: 0, m: 0 };
    let lastViewKey = null;
    let calFocusISO = null;

    function refreshBounds() {
      todayISO = isoDay(0);
      minISO = isoDay(leadDays);
      maxISO = isoDay(MAX_DAYS_OUT);
      selectMaxISO = addMonthsISO(new Date(), 2);
      const minParts = minISO.split('-');
      const maxParts = maxISO.split('-');
      minKey = monthKeyOf(Number(minParts[0]), Number(minParts[1]) - 1);
      maxKey = monthKeyOf(Number(maxParts[0]), Number(maxParts[1]) - 1);
    }

    function clampView() {
      const key = monthKeyOf(view.y, view.m);
      const parts = (key < minKey ? minISO : maxISO).split('-');
      if (key < minKey || key > maxKey) {
        view.y = Number(parts[0]);
        view.m = Number(parts[1]) - 1;
      }
    }

    refreshBounds();
    clampView();

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const stepLinks = Array.from(root.querySelectorAll('[data-stepper] [data-step-link]'));
    const continueLink = root.querySelector('[data-continue]');
    const notice = root.querySelector('[data-notice]');
    const requestForm = root.querySelector('#booking-request-form');
    const requestBody = root.querySelector('[data-request-body]');
    const requestPhone = root.querySelector('[data-request-phone]');
    const requestName = root.querySelector('[data-request-name]');
    const requestEmail = root.querySelector('[data-request-email]');
    const emptyMessage = root.querySelector('[data-empty]');
    const summaryService = root.querySelector('[data-summary-service]');
    const serviceName = root.querySelector('[data-service-name]');
    const serviceDesc = root.querySelector('[data-service-desc]');
    const serviceIncludes = root.querySelector('[data-service-includes]');
    const interestRow = root.querySelector('[data-row-interest]');
    const interestName = root.querySelector('[data-interest-name]');
    const vehicleRow = root.querySelector('[data-row-vehicle]');
    const vehicleName = root.querySelector('[data-vehicle-name]');
    const durationRow = root.querySelector('[data-row-duration]');
    const durationValue = root.querySelector('[data-duration]');
    const quoteRow = root.querySelector('[data-row-quote]');
    const schedRow = root.querySelector('[data-row-sched]');
    const schedName = root.querySelector('[data-sched-name]');
    const sched2Row = root.querySelector('[data-row-sched2]');
    const sched2Name = root.querySelector('[data-sched2-name]');
    const locRow = root.querySelector('[data-row-loc]');
    const locName = root.querySelector('[data-loc-name]');
    const suburbWrap = root.querySelector('[data-suburb-wrap]');
    const suburbInput = root.querySelector('[data-suburb]');
    const statusLine = root.querySelector('[data-sched-status]');
    const calMonth = root.querySelector('[data-cal-month]');
    const calDays = root.querySelector('[data-cal-days]');
    const calPrev = root.querySelector('[data-cal-prev]');
    const calNext = root.querySelector('[data-cal-next]');

    function service() {
      return store.service || null;
    }

    function vehicle() {
      return store.vehicle || null;
    }

    function windowText(value) {
      if (!value) return '';
      const hint = WINDOW_TIMES[value];
      return hint ? value + ' ' + hint : value;
    }

    function schedText(date, windowLabel) {
      const when = fmtDate(date);
      const window = windowText(windowLabel);
      if (when && window) return when + ' · ' + window;
      return when || window || '';
    }

    function buildRequestText(ref) {
      const lines = ['Booking reference: ' + ref, '', 'New STIKKYS booking request', ''];
      const current = service();
      if (current) {
        lines.push('Detail: ' + (current.title || ''));
        if (current.duration) lines.push('Duration: ' + current.duration);
      }
      if (store.interest && store.interest.text) lines.push('Asked about: ' + store.interest.text);
      const currentV = vehicle();
      if (currentV) lines.push('Vehicle: ' + (currentV.label || ''));
      lines.push('');
      const first = schedText(sched.date1, sched.window1);
      if (first) lines.push('First preference: ' + first);
      const second = schedText(sched.date2, sched.window2);
      if (second) lines.push('Second preference: ' + second);
      const loc = locationText(sched);
      if (loc) lines.push('Location: ' + loc);
      return lines.join('\n');
    }

    function validateRequestPhone() {
      if (!requestPhone) return true;
      const valid = auMobileValid(requestPhone.value);
      requestPhone.setCustomValidity(valid ? '' : AU_MOBILE_MESSAGE);
      requestPhone.setAttribute('aria-invalid', valid ? 'false' : 'true');
      return valid;
    }

    function renderCalendar() {
      if (!calDays) return;
      if (calMonth) {
        calMonth.textContent = new Date(view.y, view.m, 1).toLocaleDateString('en-AU', {
          month: 'long',
          year: 'numeric'
        });
      }
      if (calPrev) calPrev.disabled = monthKeyOf(view.y, view.m) <= minKey;
      if (calNext) calNext.disabled = monthKeyOf(view.y, view.m) >= maxKey;

      root.querySelectorAll('[data-active-pref]').forEach((button) => {
        const pref = button.getAttribute('data-active-pref');
        button.setAttribute('aria-pressed', Number(pref) === activePref ? 'true' : 'false');
      });
      root.querySelectorAll('[data-cal-pref]').forEach((el) => {
        const iso = el.getAttribute('data-cal-pref') === '1' ? sched.date1 : sched.date2;
        el.textContent = fmtDate(iso) || 'Not set';
      });
      root.querySelectorAll('[data-pick-date]').forEach((button) => {
        const pref = button.getAttribute('data-pick-date');
        const iso = pref === '1' ? sched.date1 : sched.date2;
        const label = button.querySelector('[data-pick-label="' + pref + '"]');
        if (label) label.textContent = fmtDate(iso) || 'Pick a date';
        button.setAttribute('data-set', iso ? 'true' : 'false');
      });

      calDays.innerHTML = '';
      const offset = (new Date(view.y, view.m, 1).getDay() + 6) % 7;
      const count = new Date(view.y, view.m + 1, 0).getDate();
      for (let i = 0; i < offset; i += 1) {
        const empty = document.createElement('span');
        empty.className = 'booking-day booking-day--empty';
        empty.setAttribute('aria-hidden', 'true');
        calDays.appendChild(empty);
      }
      for (let day = 1; day <= count; day += 1) {
        const iso = view.y + '-' + pad(view.m + 1) + '-' + pad(day);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'booking-day';
        button.setAttribute('data-day', iso);
        button.textContent = String(day);
        const is1 = sched.date1 === iso;
        const is2 = sched.date2 === iso;
        if (is1 && is2) button.setAttribute('data-pref', 'both');
        else if (is1) button.setAttribute('data-pref', '1');
        else if (is2) button.setAttribute('data-pref', '2');
        if (iso === todayISO) button.setAttribute('data-today', 'true');
        button.setAttribute('aria-pressed', is1 || is2 ? 'true' : 'false');
        let label = new Date(view.y, view.m, day).toLocaleDateString('en-AU', {
          weekday: 'long',
          day: 'numeric',
          month: 'long'
        });
        if (is1 && is2) label += ', first and second preference';
        else if (is1) label += ', first preference';
        else if (is2) label += ', second preference';
        if (iso < minISO || iso > selectMaxISO) {
          button.disabled = true;
          label += iso > selectMaxISO ? ', not open for booking yet' : ', unavailable';
        }
        button.setAttribute('aria-label', label);
        calDays.appendChild(button);
      }

      const key = monthKeyOf(view.y, view.m);
      if (key !== lastViewKey) {
        lastViewKey = key;
        if (!reduceMotion.matches) {
          calDays.classList.remove('is-anim');
          void calDays.offsetWidth;
          calDays.classList.add('is-anim');
        }
      }
      if (calFocusISO) {
        const again = calDays.querySelector('[data-day="' + calFocusISO + '"]');
        if (again && !again.disabled) again.focus({ preventScroll: true });
      }
    }

    function persistSched() {
      saveStore({ sched: sched });
      store.sched = sched;
      clearNotice();
    }

    let noticeKind = null;

    function say(message, kind) {
      if (!notice) return;
      notice.textContent = message;
      notice.hidden = false;
      noticeKind = kind || 'nag';
    }

    function clearNotice() {
      if (!notice || noticeKind !== 'nag') return;
      notice.hidden = true;
      notice.textContent = '';
      noticeKind = null;
    }

    function nag(selector) {
      const target = root.querySelector(selector);
      if (!target) return;
      target.classList.remove('is-nag');
      void target.offsetWidth;
      target.classList.add('is-nag');
    }

    function enableContinue() {
      if (!continueLink) return;
      continueLink.removeAttribute('aria-disabled');
      continueLink.removeAttribute('tabindex');
    }

    function resyncFromStore() {
      const fresh = loadStore();
      store.service = fresh.service || null;
      store.vehicle = fresh.vehicle || null;
      store.interest = fresh.interest || null;
      store.sched = fresh.sched || null;
      const fs = fresh.sched || {};
      sched.date1 = fs.date1 || '';
      sched.window1 = fs.window1 || '';
      sched.date2 = fs.date2 || '';
      sched.window2 = fs.window2 || '';
      sched.location = fs.location || null;
      sched.suburb = fs.suburb || '';
      selectedServiceId = store.service ? store.service.id : null;
      selectedVehicleId = store.vehicle ? store.vehicle.id : null;
    }

    function render() {
      const currentService = service();
      const currentVehicle = vehicle();

      root.querySelectorAll('[data-service-card]').forEach((card) => {
        const selected = card.getAttribute('data-service-card') === selectedServiceId;
        card.setAttribute('data-selected', selected ? 'true' : 'false');
        const button = card.querySelector('[data-select-service]');
        if (button) {
          button.setAttribute('aria-pressed', selected ? 'true' : 'false');
          button.classList.toggle('button--primary', selected);
          button.classList.toggle('button--secondary', !selected);
          /* The button carries the state ("✓ Selected") so the card needs
             no reserved tag space below it. */
          const label = button.querySelector('.btn-text');
          const info = serviceById(card.getAttribute('data-service-card'));
          if (label) label.textContent = selected ? '✓ Selected' : 'Select ' + (info && info.title ? info.title : '');
        }
      });

      root.querySelectorAll('[data-select-vehicle]').forEach((button) => {
        const selected = button.getAttribute('data-select-vehicle') === selectedVehicleId;
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      if (emptyMessage) emptyMessage.hidden = Boolean(currentService);
      if (summaryService) summaryService.hidden = !currentService;
      if (currentService) {
        if (serviceName) serviceName.textContent = currentService.title || '';
        if (serviceDesc) serviceDesc.textContent = currentService.desc || '';
        if (serviceIncludes) {
          serviceIncludes.innerHTML = '';
          String(currentService.includes || '')
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean)
            .forEach((line) => {
              const item = document.createElement('li');
              item.textContent = line;
              serviceIncludes.appendChild(item);
            });
        }
      }
      const currentInterest = store.interest && store.interest.text ? store.interest.text : '';
      if (interestRow) interestRow.hidden = !currentInterest;
      if (interestName) interestName.textContent = currentInterest;
      if (vehicleRow) vehicleRow.hidden = !currentVehicle;
      if (currentVehicle && vehicleName) vehicleName.textContent = currentVehicle.label || '';
      if (durationRow) durationRow.hidden = !currentService || !currentService.duration;
      if (durationValue) durationValue.textContent = currentService && currentService.duration ? currentService.duration : '';
      if (quoteRow) quoteRow.hidden = !currentService;

      const sched1 = schedText(sched.date1, sched.window1);
      if (schedRow) schedRow.hidden = !sched1;
      if (schedName) schedName.textContent = sched1;
      const sched2 = schedText(sched.date2, sched.window2);
      if (sched2Row) sched2Row.hidden = !sched2;
      if (sched2Name) sched2Name.textContent = sched2;
      let loc = '';
      if (sched.location === 'mobile') {
        loc = (sched.suburb || '').trim() ? 'Mobile — ' + sched.suburb.trim() : 'Mobile';
      } else if (sched.location === 'inhouse') {
        loc = 'In-house';
      }
      if (locRow) locRow.hidden = !loc;
      if (locName) locName.textContent = loc;

      root.querySelectorAll('[data-window]').forEach((button) => {
        const group = button.getAttribute('data-window');
        const value = button.getAttribute('data-value');
        const active = group === '1' ? sched.window1 === value : sched.window2 === value;
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      root.querySelectorAll('[data-location]').forEach((button) => {
        const active = button.getAttribute('data-location') === sched.location;
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      root.querySelectorAll('[data-pref]').forEach((card) => {
        const pref = card.getAttribute('data-pref');
        const done = pref === '1' ? Boolean(sched.date1 && sched.window1) : Boolean(sched.date2 && sched.window2);
        card.setAttribute('data-complete', done ? 'true' : 'false');
      });
      const mobileLocation = sched.location === 'mobile';
      if (suburbWrap) suburbWrap.hidden = !mobileLocation;
      if (suburbInput) {
        if (suburbInput.value !== sched.suburb) suburbInput.value = sched.suburb || '';
        if (mobileLocation) suburbInput.setAttribute('required', 'required');
        else suburbInput.removeAttribute('required');
        suburbInput.setAttribute(
          'aria-invalid',
          mobileLocation && (sched.suburb || '').trim() && !auPostcodeValid(sched.suburb) ? 'true' : 'false'
        );
      }
      if (statusLine) {
        if (schedValidOf(sched)) {
          statusLine.setAttribute('data-state', 'ready');
          statusLine.textContent = 'Looking good — request your booking below and we confirm availability by text.';
        } else {
          statusLine.setAttribute('data-state', 'todo');
          statusLine.textContent = 'Almost there — add ' + missingSchedItems(sched).join(' + ') + ' to finish your request.';
        }
      }

      renderCalendar();

      stepLinks.forEach((link) => {
        const linkStep = Number(link.getAttribute('data-step-link'));
        const reachable = linkStep === 1 || (linkStep === 2 && Boolean(currentService)) || (linkStep === 3 && Boolean(currentService && currentVehicle));
        link.removeAttribute('aria-current');
        /* Display follows POSITION, never memory: behind you reads done,
           your step reads current, ahead stays grey even when saved. */
        let status = 'todo';
        if (linkStep === step) {
          status = 'current';
          link.setAttribute('aria-current', 'step');
        } else if (linkStep < step) {
          status = 'done';
        }
        link.setAttribute('data-state', status);
        if (link.parentElement) link.parentElement.setAttribute('data-state', status);
        if (reachable || linkStep === step || step === 4) link.removeAttribute('aria-disabled');
        else link.setAttribute('aria-disabled', 'true');
      });

      if (continueLink) {
        const ready = step === 1 ? Boolean(selectedServiceId) : Boolean(selectedVehicleId);
        if (ready) {
          continueLink.removeAttribute('aria-disabled');
          continueLink.removeAttribute('tabindex');
        } else {
          continueLink.setAttribute('aria-disabled', 'true');
          continueLink.setAttribute('tabindex', '-1');
        }
      }
    }

    function shiftMonth(direction) {
      view.m += direction;
      if (view.m < 0) {
        view.m = 11;
        view.y -= 1;
      } else if (view.m > 11) {
        view.m = 0;
        view.y += 1;
      }
    }

    function pickDay(iso) {
      if (activePref === 1) {
        if (sched.date1 === iso) {
          sched.date1 = '';
        } else {
          sched.date1 = iso;
          if (!sched.date2) activePref = 2;
        }
      } else if (sched.date2 === iso) {
        sched.date2 = '';
      } else {
        sched.date2 = iso;
      }
      persistSched();
    }

    root.addEventListener('click', (event) => {
      const guard = event.target.closest('[data-step-link][aria-disabled="true"], [data-continue][aria-disabled="true"]');
      if (guard) {
        event.preventDefault();
        if (step === 1 && !selectedServiceId) {
          say('Pick a detail above first, then hit Continue.');
          nag('.booking-services');
        } else if (step === 2 && !selectedVehicleId) {
          say('Pick your vehicle type above first, then hit Continue.');
          nag('.booking-chips');
        }
        return;
      }
      const newRequest = event.target.closest('[data-new-request]');
      if (newRequest) {
        try {
          localStorage.removeItem(STORE_KEY);
          localStorage.removeItem(RECEIPT_KEY);
        } catch (_error) {
          /* keys already gone; the link still navigates */
        }
        return;
      }
      const serviceButton = event.target.closest('[data-select-service]');
      if (serviceButton) {
        selectedServiceId = serviceButton.getAttribute('data-select-service');
        clearNotice();
        enableContinue();
        const found = serviceById(selectedServiceId);
        if (found) {
          store.service = {
            id: found.id,
            title: found.title,
            desc: found.desc,
            includes: found.includes,
            duration: found.duration
          };
          /* A manual pick is the newest intent: the tapped option only
             survives while its branch stays selected. */
          if (store.interest && store.interest.serviceId !== selectedServiceId) store.interest = null;
          saveStore({ service: store.service, interest: store.interest });
        }
        render();
        return;
      }
      const vehicleButton = event.target.closest('[data-select-vehicle]');
      if (vehicleButton) {
        selectedVehicleId = vehicleButton.getAttribute('data-select-vehicle');
        clearNotice();
        enableContinue();
        const found = vehicleById(selectedVehicleId);
        if (found) {
          store.vehicle = { id: found.id, label: found.label };
          saveStore({ vehicle: store.vehicle });
        }
        render();
        return;
      }
      const windowButton = event.target.closest('[data-window]');
      if (windowButton) {
        const value = windowButton.getAttribute('data-value');
        if (windowButton.getAttribute('data-window') === '1') sched.window1 = value;
        else sched.window2 = value;
        persistSched();
        render();
        return;
      }
      const locButton = event.target.closest('[data-location]');
      if (locButton) {
        sched.location = locButton.getAttribute('data-location');
        persistSched();
        render();
        if (sched.location === 'mobile') {
          const suburb = root.querySelector('[data-suburb]');
          if (suburb) suburb.focus({ preventScroll: true });
          else {
            const wrap = root.querySelector('[data-suburb-wrap]');
            const input = wrap ? wrap.querySelector('input') : null;
            if (input) input.focus({ preventScroll: true });
          }
        }
        return;
      }
      const clearButton = event.target.closest('[data-clear-pref]');
      if (clearButton) {
        sched.date2 = '';
        sched.window2 = '';
        persistSched();
        render();
        return;
      }
      const dayButton = event.target.closest('[data-day]');
      if (dayButton && !dayButton.disabled) {
        pickDay(dayButton.getAttribute('data-day'));
        render();
        return;
      }
      const prevButton = event.target.closest('[data-cal-prev]');
      if (prevButton && !prevButton.disabled) {
        shiftMonth(-1);
        render();
        return;
      }
      const nextButton = event.target.closest('[data-cal-next]');
      if (nextButton && !nextButton.disabled) {
        shiftMonth(1);
        render();
        return;
      }
      const todayButton = event.target.closest('[data-cal-today]');
      if (todayButton) {
        refreshBounds();
        const now = new Date();
        view.y = now.getFullYear();
        view.m = now.getMonth();
        clampView();
        render();
        return;
      }
      const prefButton = event.target.closest('[data-active-pref]');
      if (prefButton) {
        activePref = prefButton.getAttribute('data-active-pref') === '2' ? 2 : 1;
        render();
        return;
      }
      const jumpButton = event.target.closest('[data-pick-date]');
      if (jumpButton) {
        activePref = jumpButton.getAttribute('data-pick-date') === '2' ? 2 : 1;
        const iso = activePref === 1 ? sched.date1 : sched.date2;
        if (iso) {
          const parts = iso.split('-');
          view.y = Number(parts[0]);
          view.m = Number(parts[1]) - 1;
        }
        render();
        const cal = root.querySelector('[data-cal]');
        if (cal) {
          cal.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
          if (!reduceMotion.matches) {
            cal.classList.remove('is-pulse');
            void cal.offsetWidth;
            cal.classList.add('is-pulse');
          }
        }
      }
    });

    root.addEventListener('focusin', (event) => {
      const day = event.target.closest('[data-day]');
      calFocusISO = day ? day.getAttribute('data-day') : null;
    });

    root.addEventListener('focusout', () => {
      calFocusISO = null;
    });

    root.addEventListener('input', (event) => {
      const phone = event.target.closest('[data-request-phone]');
      if (phone) {
        phone.value = capDigits(phone.value, 10);
        validateRequestPhone();
      }

      const suburb = event.target.closest('[data-suburb]');
      if (suburb) {
        suburb.value = postcodeDigits(suburb.value);
        sched.suburb = suburb.value;
        persistSched();
        render();
      }
    });

    window.addEventListener('pageshow', (event) => {
      if (event && event.persisted) {
        resyncFromStore();
        render();
      }
    });

    if (requestForm) {
      requestForm.addEventListener('submit', (event) => {
        if (!schedValidOf(sched)) {
          event.preventDefault();
          if (sched.location === 'mobile' && !auPostcodeValid(sched.suburb)) {
            say('Enter a valid 4-digit postcode for the mobile location.');
          } else {
            say('Almost there — add ' + missingSchedItems(sched).join(' + ') + ' to finish your request.');
          }
          if (!sched.date1) nag('[data-cal]');
          else if (!sched.window1) nag('.booking-sched-grid');
          else if (!sched.location) nag('.booking-loc-grid');
          else nag('[data-suburb-wrap]');
          return;
        }
        if (!validateRequestPhone()) {
          event.preventDefault();
          if (typeof requestPhone.reportValidity === 'function') requestPhone.reportValidity();
          requestPhone.focus({ preventScroll: true });
          return;
        }
        const ref = makeReference();
        if (requestBody) requestBody.value = buildRequestText(ref);
        saveReceipt({
          ref: ref,
          sentAt: Date.now(),
          name: requestName ? requestName.value.trim() : '',
          phone: requestPhone ? requestPhone.value.trim() : '',
          email: requestEmail ? requestEmail.value.trim() : '',
          service: service(),
          vehicle: vehicle(),
          interest: store.interest || null,
          sched: Object.assign({}, sched)
        });
      });
    }

    if (!storageWorks()) {
      say('Heads up — this browser is not saving your picks, so choose again if the next page looks empty.', 'warn');
    } else {
      let need = null;
      try {
        need = new URLSearchParams(window.location.search).get('need');
      } catch (_error) {
        need = null;
      }
      if ((need === 'service' && step === 1) || (need === 'vehicle' && step === 2)) {
        say('Your last pick did not carry over — choose again to continue.', 'warn');
        try {
          const clean = window.location.pathname || STEP_URLS[step];
          window.history.replaceState(null, '', clean);
        } catch (_error) {
          /* leave the flag; harmless */
        }
      }
    }

    setInterval(() => {
      if (isoDay(0) !== todayISO) {
        refreshBounds();
        clampView();
        render();
      }
    }, 60000);

    function fillConfirm() {
      if (step !== 4 || !receiptValid(receipt)) return;
      const setRow = (hook, text) => {
        const val = root.querySelector('[' + hook + ']');
        if (!val) return;
        val.textContent = text || '';
        const row = val.closest('li');
        if (row) row.hidden = !text;
      };
      const refEl = root.querySelector('[data-confirm-ref]');
      if (refEl) refEl.textContent = receipt.ref || '';
      const thanksEl = root.querySelector('[data-confirm-thanks]');
      const firstName = String(receipt.name || '').trim().split(/\s+/)[0] || '';
      if (thanksEl) {
        if (firstName && receipt.phone) {
          thanksEl.textContent = 'Thanks ' + firstName + ' — we’ll text ' + receipt.phone + ' within 2 business hours.';
          thanksEl.hidden = false;
        } else {
          thanksEl.hidden = true;
        }
      }
      const rSched = receipt.sched || {};
      const rService = receipt.service || null;
      setRow(
        'data-confirm-service',
        rService && rService.title ? rService.title + (rService.duration ? ' · ' + rService.duration : '') : ''
      );
      const rInterest = receipt.interest || null;
      setRow('data-confirm-interest', rInterest && rInterest.text ? rInterest.text : '');
      setRow('data-confirm-vehicle', receipt.vehicle ? receipt.vehicle.label || '' : '');
      setRow('data-confirm-sched1', schedText(rSched.date1, rSched.window1));
      setRow('data-confirm-sched2', schedText(rSched.date2, rSched.window2));
      setRow('data-confirm-loc', locationText(rSched));
      setRow('data-confirm-name', receipt.name || '');
      setRow('data-confirm-phone', receipt.phone || '');
      setRow('data-confirm-email', receipt.email || '');
      const wrap = root.querySelector('[data-confirm-recap]');
      if (wrap) wrap.hidden = false;
    }

    fillConfirm();
    render();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
